import { Injectable, computed, effect, signal } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { jwtDecode } from 'jwt-decode';
import { ApiService } from '../api/api.service';
import { SseService } from '../api/sse.service';
import { Chat } from '../../models/chat.model';
import { Message } from '../../models/message.model';
import { Numero } from '../../models/numero.model';
import { Status } from '../../models/status.model';
import { Conversation } from '../../models/conversation.model';
import { SendPlainMessageRequest } from '../../models/message.interface';
import { NotificationService } from './toast-service.service';
import { NotificationSystemService } from './notification-system.service';
import { NotificationAudioService } from './notification-audio.service';
import { UserPresenceActivityService } from './user-presence-activity.service';
import { InternalChatStoreService } from './internal-chat-store.service';
import { InternalChatMessage } from '../../models/internal-chat.model';
import {
  ConversationDerivacionSseEvent,
  ConversationDerivacionPopupSseEvent,
  ConversationStateSseEvent,
  SidebarSseEvent,
  SenderUnreadSseEvent,
} from '../../models/sse-chatlink-events.model';
import {
  DerivacionRequest,
  DerivacionResponse,
} from '../../models/derivacion.model';

interface SelfAssignmentPopupSuppression {
  username: string;
  conversacionId: number | null;
  expiresAt: number;
}

@Injectable({
  providedIn: 'root',
})
export class ChatRealtimeStoreService {
  private initialized = false;
  private readonly unreadReconcileDelayMs = 1200;
  private readonly chatsPageSize = 15;
  private readonly initialMessagesPageSize = 25;
  private readonly olderMessagesPageSize = 20;
  private unreadReconcileTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private incomingAudioHydrationTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private manualUnreadPendingWaIds = new Set<string>();
  private readonly selfAssignmentPopupSuppressions = new Map<string, SelfAssignmentPopupSuppression>();
  private readonly selfAssignmentPopupSuppressionDurationMs = 30000;
  private notifiedExternalMessageIds = new Set<string>();
  private notifiedInternalMessageIds = new Set<string>();
  private pendingInternalNotifications: InternalChatMessage[] = [];
  private currentUsernameState = signal<string>('');

  private chatsState = signal<Chat[]>([]);
  private listaNumerosState = signal<Numero[]>([]);
  private numeroActualState = signal<Numero | null>(null);
  private filtroState = signal<string>('Todos');
  private unreadCountState = signal<number>(0);
  private unreadCountRequestSeq = 0;
  private messagesRequestSeq = 0;

  // Active Chat State
  private activeChatWaIdState = signal<string | null>(null);
  private selectedChatState = signal<Chat | null>(null);
  private messagesState = signal<Message[]>([]);
  private isLoadingMessagesState = signal<boolean>(false);
  private isLoadingChatsState = signal<boolean>(false);
  private chatsLoadingRequests = 0;
  private chatsCurrentPageState = signal<number>(0);
  private hasMoreChatsState = signal<boolean>(true);
  private actualConversationNumberState = signal<number>(0);
  private ultimaConverRecuperadaState = signal<number>(0);
  private hasMoreMessagesState = signal<boolean>(false);
  private olderMessagesExhaustedState = signal<boolean>(false);
  private olderMessagesConversationIdState = signal<number>(0);
  private nextBeforeMessageIdState = signal<number | string | null>(null);
  private nextBeforeTimestampState = signal<string | null>(null);
  public mustClearCasillaMensaje = signal<boolean>(false);

  readonly chats = this.chatsState.asReadonly();
  readonly listaNumeros = this.listaNumerosState.asReadonly();
  readonly numeroActual = this.numeroActualState.asReadonly();
  readonly unreadCount = computed(() =>
    this.chatsState().reduce(
      (count, chat) => count + (chat?.sender?.mensajesNuevos ? 1 : 0),
      0,
    ),
  );

  readonly totalUnreadCount = this.unreadCountState.asReadonly();

  readonly activeChatWaId = this.activeChatWaIdState.asReadonly();
  readonly selectedChat = this.selectedChatState.asReadonly();
  readonly messages = this.messagesState.asReadonly();
  readonly isLoadingMessages = this.isLoadingMessagesState.asReadonly();
  readonly isLoadingChats = this.isLoadingChatsState.asReadonly();
  readonly hasMoreChats = this.hasMoreChatsState.asReadonly();
  readonly hasMoreMessages = this.hasMoreMessagesState.asReadonly();
  readonly actualConversationNumber =
    this.actualConversationNumberState.asReadonly();
  readonly ultimaConverRecuperada =
    this.ultimaConverRecuperadaState.asReadonly();

  constructor(
    private apiService: ApiService,
    private sseService: SseService,
    private titleService: Title,
    private toastService: NotificationService,
    private notificationSystemService: NotificationSystemService,
    private notificationAudioService: NotificationAudioService,
    private userPresenceActivityService: UserPresenceActivityService,
    private internalChatStore: InternalChatStoreService,
  ) {
    this.loadCurrentUserFromToken();

    effect(() => {
      const currentUserId = this.internalChatStore.currentUserId();
      if (currentUserId === null) {
        this.pendingInternalNotifications = [];
        return;
      }

      this.procesarNotificacionesInternasPendientes();
    });

    // Mensajes de clientes - hacer sonido si no es type 'menu'
    this.sseService.messageReceived.subscribe((message: Message) => {
      this.emitirSonidoCliente(message);
      this.applyIncomingMessage(message);
    });

    // Mensajes propios - NO hacer sonido
    this.sseService.myMessageReceived.subscribe((message: Message) => {
      this.applyIncomingMessage(message);
    });

    // Mensajes internos - solo hacer sonido si NO es del usuario actual
    this.sseService.internalMessageReceived.subscribe((message: InternalChatMessage) => {
      this.emitirSonidoInterno(message);
    });

    this.sseService.statusReceived.subscribe((status: Status) => {
      this.applyStatusUpdate(status);
    });

    this.sseService.conversationStateChanged.subscribe(
      (event: ConversationStateSseEvent) => {
        this.applyConversationStateChanged(event);
      },
    );

    this.sseService.senderUnreadChanged.subscribe(
      (event: SenderUnreadSseEvent) => {
        this.applySenderUnreadChanged(event);
        this.refreshUnreadCount();
      },
    );

    this.sseService.conversationDerivacionReceived.subscribe(
      (event: ConversationDerivacionSseEvent) => {
        this.applyConversationDerivacion(event);
      },
    );

    this.sseService.sidebarChanged.subscribe((event: SidebarSseEvent) => {
      this.applySidebarChanged(event);
      this.refreshUnreadCount();
    });

    this.sseService.reconnected.subscribe(() => {
      this.refreshChats(true);
      this.refreshUnreadCount();
      const activeWaId = this.activeChatWaIdState();
      if (activeWaId) {
        this.loadMessages(activeWaId, undefined, true).catch((error) => {
          console.error('Error reloading active chat after SSE reconnect:', error);
        });
      }
    });
  }

  init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.sseService.connect();
    this.loadNumerosAndChats();
    this.refreshUnreadCount();
  }

  setFiltro(filtro: string): void {
    this.filtroState.set(filtro);
    this.refreshChats();
  }

  setActiveChat(chat: Chat | null): void {
    if (!chat) {
      this.cancelMessagesRequest();
      this.activeChatWaIdState.set(null);
      this.selectedChatState.set(null);
      this.resetMessagesState();
      return;
    }

    // Mark as read immediately when selecting
    const chatWaId = this.getChatWaId(chat);
    if (!chatWaId) {
      this.cancelMessagesRequest();
      this.activeChatWaIdState.set(null);
      this.selectedChatState.set(null);
      this.resetMessagesState();
      return;
    }

    this.manualUnreadPendingWaIds.delete(chatWaId);

    if (chat.sender?.mensajesNuevos && chat.accessMode === 'FULL') {
      this.syncMarkAsRead(chat);
    }

    this.activeChatWaIdState.set(chatWaId);
    this.selectedChatState.set(chat);
    this.mustClearCasillaMensaje.set(true);
    this.loadMessages(chatWaId, chat.sender?.number || undefined).catch(
      (error) => {
        console.error('Error loading selected chat messages:', error);
        this.toastService.notify(
          'No se pudieron cargar los mensajes del chat.',
          'error',
        );
      },
    );
  }

  clearActiveChat(): void {
    this.setActiveChat(null);
  }

  syncMarkAsRead(chat: Chat): void {
    const waId = this.getChatWaId(chat);
    if (waId && !this.manualUnreadPendingWaIds.has(waId)) {
      this.markAsReadByWaId(waId);
    }
  }

  markAsReadByWaId(waId: string): void {
    const normalizedWaId = this.normalizeWaId(waId);
    if (!normalizedWaId) {
      return;
    }

    this.manualUnreadPendingWaIds.delete(normalizedWaId);
    this.clearUnreadReconcileTimer(normalizedWaId);

    this.apiService.marcarMensajeLeidoByWaId(waId).subscribe({
      next: () => {
        // Source of truth remains senderUnreadChanged via SSE. If it does not arrive
        // quickly, reconcile with a focused refresh.
        this.scheduleUnreadReconcile(normalizedWaId);
      },
      error: (error) => {
        console.error('Error marking messages as read:', error);
      },
    });
  }

  markAsUnreadByWaId(waId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const normalizedWaId = this.normalizeWaId(waId);
      if (!normalizedWaId) {
        reject(new Error('Invalid waId'));
        return;
      }

      this.clearUnreadReconcileTimer(normalizedWaId);
      this.manualUnreadPendingWaIds.add(normalizedWaId);
      this.setUnreadStateByWaId(normalizedWaId, true);

      const activeWaId = this.normalizeWaId(this.activeChatWaIdState() || '');
      if (activeWaId === normalizedWaId) {
        this.cancelMessagesRequest();
        this.activeChatWaIdState.set(null);
        this.selectedChatState.set(null);
        this.resetMessagesState();
      }

      this.apiService.marcarChatNoLeidoByWaId(waId).subscribe({
        next: () => {
          // SSE will emit senderUnreadChanged and update the state.
          // Keep the local pending guard until the user explicitly reopens the chat.
          resolve();
        },
        error: (error) => {
          this.manualUnreadPendingWaIds.delete(normalizedWaId);
          this.setUnreadStateByWaId(normalizedWaId, false);
          console.error('Error marking chat as unread:', error);
          reject(error);
        },
      });
    });
  }

  refreshChats(silent = false): void {
    if (!silent) this.startChatsLoading();
    this.chatsCurrentPageState.set(0);

    this.apiService
      .getChats(this.filtroState(), 0, this.chatsPageSize)
      .subscribe({
      next: (page) => {
        this.chatsCurrentPageState.set(page.currentPage ?? 0);
        this.hasMoreChatsState.set(!!page.hasNext);
        const backendFilteredChats = this.applyManualUnreadState(
          page.content || [],
        );
        this.chatsState.set(backendFilteredChats);

        // If active chat is still here but states changed, update selectedChat pointer
        const active = this.activeChatWaIdState();
        if (active) {
          const updatedActive = backendFilteredChats.find(
            (c) => this.getChatWaId(c) === active,
          );
          if (updatedActive) {
            // Update selectedChat state to reflect new data from backend (like estadoConversacion)
            this.selectedChatState.set(updatedActive);
          } else {
            const selected = this.selectedChatState();
            const selectedConversationId = Number(
              selected?.conversacion?.id ?? 0,
            );

            // Keep local unsynced contacts (conversation id 0), but clear chats that were removed by server filters.
            if (selectedConversationId > 0 && !page.hasNext) {
              this.cancelMessagesRequest();
              this.activeChatWaIdState.set(null);
              this.selectedChatState.set(null);
              this.resetMessagesState();
            }
          }
        }

        this.updateTitle();
        if (!silent) this.finishChatsLoading();
      },
      error: () => {
        this.hasMoreChatsState.set(false);
        this.updateTitle();
        if (!silent) this.finishChatsLoading();
      },
    });
  }

  loadMoreChats(): void {
    if (!this.hasMoreChatsState() || this.isLoadingChatsState()) {
      return;
    }

    this.startChatsLoading();
    const nextPage = this.chatsCurrentPageState() + 1;

    this.apiService
      .getChats(this.filtroState(), nextPage, this.chatsPageSize)
      .subscribe({
        next: (page) => {
          this.chatsCurrentPageState.set(page.currentPage ?? nextPage);
          this.hasMoreChatsState.set(!!page.hasNext);
          const incomingChats = this.applyManualUnreadState(page.content || []);
          this.chatsState.update((currentChats) =>
            this.mergeUniqueChats(currentChats, incomingChats),
          );
          this.updateTitle();
          this.finishChatsLoading();
        },
        error: () => {
          this.finishChatsLoading();
        },
      });
  }

  private startChatsLoading(): void {
    this.chatsLoadingRequests += 1;
    this.isLoadingChatsState.set(true);
  }

  private finishChatsLoading(): void {
    this.chatsLoadingRequests = Math.max(0, this.chatsLoadingRequests - 1);
    this.isLoadingChatsState.set(this.chatsLoadingRequests > 0);
  }

  private applyManualUnreadState(chats: Chat[]): Chat[] {
    return (chats || []).map((chat) => {
      const chatWaId = this.getChatWaId(chat);
      if (!chatWaId || !this.manualUnreadPendingWaIds.has(chatWaId)) {
        return chat;
      }

      return {
        ...chat,
        sender: {
          ...chat.sender,
          mensajesNuevos: true,
        },
      };
    });
  }

  private mergeUniqueChats(currentChats: Chat[], incomingChats: Chat[]): Chat[] {
    const knownKeys = new Set(
      currentChats.map((chat, index) => this.getChatUniqueKey(chat, index)),
    );
    const uniqueIncoming = incomingChats.filter((chat, index) => {
      const key = this.getChatUniqueKey(chat, index);
      if (knownKeys.has(key)) {
        return false;
      }

      knownKeys.add(key);
      return true;
    });

    return [...currentChats, ...uniqueIncoming];
  }

  private getChatUniqueKey(chat: Chat, fallbackIndex: number = 0): string {
    const waId = this.getChatWaId(chat);
    const conversationId = Number(chat?.conversacion?.id ?? 0);

    if (waId && conversationId > 0) {
      return `${waId}:${conversationId}`;
    }

    return waId || String(conversationId || fallbackIndex);
  }

  updateClientData(
    updatedClient: Pick<
      Chat['sender'],
      'waId' | 'name' | 'correoElectronico' | 'notas'
    >,
  ): void {
    const updatedWaId = this.normalizeWaId(updatedClient.waId);
    const updateChat = (chat: Chat): Chat => {
      if (this.getChatWaId(chat) !== updatedWaId) {
        return chat;
      }

      return {
        ...chat,
        sender: {
          ...chat.sender,
          ...updatedClient,
        },
      };
    };

    this.chatsState.update((currentChats) => currentChats.map(updateChat));

    // The main view reads selectedChat directly. Updating only the list left
    // an open conversation displaying stale contact data until a full reload.
    const selectedChat = this.selectedChatState();
    if (selectedChat) {
      this.selectedChatState.set(updateChat(selectedChat));
    }
  }

  private refreshUnreadCount(): void {
    const requestSeq = ++this.unreadCountRequestSeq;
    this.apiService.getOpenUnreadChatCount().subscribe({
      next: ({ unreadCount }) => {
        if (requestSeq !== this.unreadCountRequestSeq) {
          return;
        }
        this.unreadCountState.set(Math.max(0, Number(unreadCount) || 0));
      },
      error: (error) => console.error('Error refreshing filtered chat count:', error),
    });
  }

  updateContactReachability(
    waId: string,
    whatsappReachabilityStatus: 'UNKNOWN' | 'VALID' | 'REJECTED',
    lastWhatsappValidationAt: string | null,
  ): void {
    const normalizedWaId = this.normalizeWaId(waId);
    const updateChat = (chat: Chat): Chat =>
      this.getChatWaId(chat) === normalizedWaId
        ? {
            ...chat,
            sender: {
              ...chat.sender,
              whatsappReachabilityStatus,
              lastWhatsappValidationAt,
            },
          }
        : chat;

    this.chatsState.update((currentChats) => currentChats.map(updateChat));
    const selected = this.selectedChatState();
    if (selected && this.getChatWaId(selected) === normalizedWaId) {
      this.selectedChatState.set(updateChat(selected));
    }
  }

  private setUnreadStateByWaId(normalizedWaId: string, unread: boolean): void {
    this.chatsState.update((currentChats) => {
      const updated = currentChats.map((chat) => {
        if (this.getChatWaId(chat) !== normalizedWaId) {
          return chat;
        }

        const updatedChat = {
          ...chat,
          sender: {
            ...chat.sender,
            mensajesNuevos: unread,
          },
        };

        return updatedChat;
      });
      return updated;
    });

    const selectedChat = this.selectedChatState();
    if (selectedChat && this.getChatWaId(selectedChat) === normalizedWaId) {
      this.selectedChatState.set({
        ...selectedChat,
        sender: {
          ...selectedChat.sender,
          mensajesNuevos: unread,
        },
      });
    }

    this.updateTitle();
  }

  loadMessages(
    waId: string,
    fallbackWaId?: string,
    preserveExisting = false,
  ): Promise<void> {
    const requestSeq = ++this.messagesRequestSeq;
    const retainCurrentMessages = preserveExisting && this.messagesState().length > 0;
    this.isLoadingMessagesState.set(true);
    if (!retainCurrentMessages) this.resetMessagesState();
    const waIdsToTry = this.buildWaIdCandidates(waId, fallbackWaId);

    return new Promise((resolve, reject) => {
      if (waIdsToTry.length === 0) {
        if (requestSeq === this.messagesRequestSeq) {
          if (!retainCurrentMessages) this.resetMessagesState();
          this.isLoadingMessagesState.set(false);
        }
        resolve();
        return;
      }

      this.loadMessagesByCandidates(
        waIdsToTry,
        0,
        requestSeq,
        resolve,
        reject,
        retainCurrentMessages,
      );
    });
  }

  private loadMessagesByCandidates(
    waIds: string[],
    index: number,
    requestSeq: number,
    resolve: () => void,
    reject: (reason?: any) => void,
    retainCurrentMessages = false,
  ): void {
    if (requestSeq !== this.messagesRequestSeq) {
      resolve();
      return;
    }

    const waId = waIds[index];

    this.apiService.getMessages(waId, this.initialMessagesPageSize).subscribe({
      next: (messagesPage) => {
        if (requestSeq !== this.messagesRequestSeq) {
          resolve();
          return;
        }
        const data = messagesPage?.content || [];
        const hasNextCandidate = index < waIds.length - 1;
        if (data.length === 0 && hasNextCandidate) {
          this.loadMessagesByCandidates(
            waIds,
            index + 1,
            requestSeq,
            resolve,
            reject,
            retainCurrentMessages,
          );
          return;
        }

        const currentMessages = this.messagesState();
        const visibleMessages = retainCurrentMessages
          ? this.mergeRefreshedMessages(data, currentMessages)
          : data;
        this.messagesState.set(visibleMessages);
        if (!retainCurrentMessages) this.applyMessageCursor(messagesPage);

        const activeConversationId = Number(
          messagesPage?.activeConversationId ??
            this.selectedChatState()?.conversacion?.id ??
            0,
        );
        if (activeConversationId > 0) {
          this.actualConversationNumberState.set(activeConversationId);
        } else if (visibleMessages.length > 0) {
          this.actualConversationNumberState.set(
            visibleMessages[visibleMessages.length - 1].conversacion?.id ?? 0,
          );
        } else {
          this.actualConversationNumberState.set(0);
        }
        this.updateLegacyOldestConversation(visibleMessages);

        this.isLoadingMessagesState.set(false);
        resolve();
      },
      error: (error) => {
        if (requestSeq !== this.messagesRequestSeq) {
          resolve();
          return;
        }
        const hasNextCandidate = index < waIds.length - 1;
        if (hasNextCandidate) {
          this.loadMessagesByCandidates(
            waIds,
            index + 1,
            requestSeq,
            resolve,
            reject,
            retainCurrentMessages,
          );
          return;
        }

        console.error('Error loading messages:', error);
        if (!retainCurrentMessages) this.resetMessagesState();
        this.isLoadingMessagesState.set(false);
        reject(error);
      },
    });
  }

  private cancelMessagesRequest(): void {
    this.messagesRequestSeq++;
    this.isLoadingMessagesState.set(false);
  }

  private buildWaIdCandidates(
    primaryWaId: string,
    secondaryWaId?: string,
  ): string[] {
    const rawCandidates = [primaryWaId, secondaryWaId]
      .map((value) => (value ?? '').trim())
      .filter((value) => !!value);

    const candidatesWithNormalized = rawCandidates.flatMap((value) => {
      const normalized = this.normalizeWaId(value);
      if (normalized && normalized !== value) {
        return [value, normalized];
      }
      return [value];
    });

    return Array.from(new Set(candidatesWithNormalized));
  }

  private normalizeWaId(value: string | null | undefined): string {
    const rawValue = (value ?? '').trim();
    if (!rawValue) {
      return '';
    }

    const digits = rawValue.replace(/\D/g, '');
    return digits || rawValue.toLowerCase();
  }

  private getChatWaId(chat: Chat | null | undefined): string {
    return this.normalizeWaId(chat?.sender?.waId || chat?.sender?.number);
  }

  private getSelectedConversationId(): number {
    return Number(this.selectedChatState()?.conversacion?.id ?? 0);
  }

  private resetMessagesState(): void {
    this.messagesState.set([]);
    this.actualConversationNumberState.set(0);
    this.ultimaConverRecuperadaState.set(0);
    this.hasMoreMessagesState.set(false);
    this.olderMessagesExhaustedState.set(false);
    this.olderMessagesConversationIdState.set(0);
    this.nextBeforeMessageIdState.set(null);
    this.nextBeforeTimestampState.set(null);
  }

  private applyMessageCursor(messagesPage: {
    hasMore?: boolean;
    conversationId?: number;
    nextBeforeMessageId?: number | string | null;
    nextBeforeTimestamp?: string | null;
  } | null | undefined): void {
    this.hasMoreMessagesState.set(!!messagesPage?.hasMore);
    this.olderMessagesConversationIdState.set(
      Number(messagesPage?.conversationId ?? 0),
    );
    this.nextBeforeMessageIdState.set(messagesPage?.nextBeforeMessageId ?? null);
    this.nextBeforeTimestampState.set(messagesPage?.nextBeforeTimestamp ?? null);
  }

  private updateLegacyOldestConversation(messages: Message[]): void {
    const firstMsg = messages[0];
    this.ultimaConverRecuperadaState.set(firstMsg?.conversacion?.id ?? 0);
  }

  private getMessageUniqueKey(message: Message, fallbackIndex: number = 0): string {
    const internalId = message.id;
    if (internalId !== null && internalId !== undefined && String(internalId)) {
      return `id:${String(internalId)}`;
    }

    if (message.messageId) {
      return `messageId:${String(message.messageId)}`;
    }

    return [
      message.conversacion?.id ?? 0,
      message.timestamp || '',
      message.body || '',
      fallbackIndex,
    ].join('|');
  }

  private mergeRefreshedMessages(
    refreshedMessages: Message[],
    currentMessages: Message[],
  ): Message[] {
    const messagesByKey = new Map<string, Message>();
    currentMessages.forEach((message, index) => {
      messagesByKey.set(this.getMessageUniqueKey(message, index), message);
    });
    refreshedMessages.forEach((message, index) => {
      const key = this.getMessageUniqueKey(message, index);
      messagesByKey.set(key, { ...messagesByKey.get(key), ...message });
    });

    return Array.from(messagesByKey.values()).sort((left, right) => {
      const leftTimestamp = Date.parse(left.timestamp);
      const rightTimestamp = Date.parse(right.timestamp);
      return Number.isFinite(leftTimestamp) && Number.isFinite(rightTimestamp)
        ? leftTimestamp - rightTimestamp
        : 0;
    });
  }

  private prependUniqueMessages(
    olderMessages: Message[],
    currentMessages: Message[],
  ): Message[] {
    const currentKeys = new Set(
      currentMessages.map((message, index) =>
        this.getMessageUniqueKey(message, index),
      ),
    );

    const uniqueOlderMessages = olderMessages.filter((message, index) => {
      const key = this.getMessageUniqueKey(message, index);
      if (currentKeys.has(key)) {
        return false;
      }

      currentKeys.add(key);
      return true;
    });

    return [...uniqueOlderMessages, ...currentMessages];
  }

  private getOldestMessageCursor(messages: Message[]): {
    beforeMessageId: number | string | null;
    beforeTimestamp: string | null;
  } {
    const oldestMessage = messages[0];
    return {
      beforeMessageId: oldestMessage?.id ?? oldestMessage?.messageId ?? null,
      beforeTimestamp: oldestMessage?.timestamp || null,
    };
  }

  loadOlderMessages(): Promise<boolean> {
    const chat = this.selectedChatState();
    const currentMessages = this.messagesState();

    const waId = this.getChatWaId(chat);
    const conversationId =
      this.olderMessagesConversationIdState() ||
      this.actualConversationNumberState();
    if (
      !chat ||
      !waId ||
      currentMessages.length === 0 ||
      !conversationId ||
      this.olderMessagesExhaustedState()
    ) {
      return Promise.resolve(false);
    }

    const oldestCursor = this.getOldestMessageCursor(currentMessages);
    const beforeTimestamp =
      this.nextBeforeTimestampState() || oldestCursor.beforeTimestamp;
    const beforeMessageId =
      this.nextBeforeMessageIdState() || oldestCursor.beforeMessageId;

    return new Promise((resolve, reject) => {
      this.apiService
        .obtenerMensajesMasAntiguos(
          waId,
          conversationId,
          beforeTimestamp,
          beforeMessageId,
          this.olderMessagesPageSize,
        )
        .subscribe({
          next: (messagesPage) => {
            const olderMessages = messagesPage?.content || [];
            this.applyMessageCursor(messagesPage);

            if (olderMessages.length === 0) {
              this.olderMessagesExhaustedState.set(!messagesPage?.hasMore);
              resolve(!!messagesPage?.hasMore);
              return;
            }

            this.messagesState.update((msgs) => {
              const mergedMessages = this.prependUniqueMessages(
                olderMessages,
                msgs,
              );
              this.updateLegacyOldestConversation(mergedMessages);
              return mergedMessages;
            });

            this.olderMessagesExhaustedState.set(!messagesPage?.hasMore);
            resolve(true);
          },
          error: (error) => {
            reject(error);
          },
        });
    });
  }

  sendMessage(
    waId: string,
    replyTo: string | null,
    body: string,
    type: 'text' | 'audio' | 'video' | 'image' | 'document' = 'text',
    ownershipConfirmed = false,
    expectedOwnerUsername: string | null = null,
  ): Promise<void> {
    const message: SendPlainMessageRequest = {
      waId: waId,
      body: body,
      type: type,
      recentlyCreated: false,
      conversationId: this.getSelectedConversationId(),
      replyTo: replyTo,
      ownershipConfirmed,
      expectedOwnerUsername,
    };

    return new Promise((resolve, reject) => {
      this.apiService.sendPlaneMessage(message).subscribe({
        next: () => resolve(),
        error: (err) => reject(err),
      });
    });
  }

  sendInternalObservation(
    waId: string,
    body: string,
    replyTo: string | null = null,
  ): Promise<void> {
    const conversationId = this.getSelectedConversationId();

    if (conversationId <= 0) {
      return Promise.reject(
        new Error('No hay una conversacion activa para agregar observaciones.'),
      );
    }

    return new Promise((resolve, reject) => {
      this.apiService
        .sendInternalObservation({
          waId,
          body,
          conversationId,
          replyTo,
        })
        .subscribe({
          next: () => resolve(),
          error: (err) => reject(err),
        });
    });
  }

  forceExitMenuActive(): Promise<any> {
    const chat = this.selectedChatState();
    if (!chat) {
      return Promise.reject(
        new Error('No hay un chat seleccionado para sacar del menu.'),
      );
    }

    const waId = this.getChatWaId(chat);
    if (!waId) {
      return Promise.reject(
        new Error('No se pudo identificar el contacto del chat.'),
      );
    }

    const conversationId = this.getSelectedConversationId();

    if (conversationId <= 0) {
      return Promise.reject(
        new Error('No hay una conversacion activa para sacar del menu.'),
      );
    }

    return new Promise((resolve, reject) => {
      this.apiService
        .forceExitMenu({
          conversationId: String(conversationId),
          waId,
        })
        .subscribe({
          next: (response) => {
            this.applyForceExitMenuResponse(response);
            resolve(response);
          },
          error: (err) => reject(err),
        });
    });
  }

  cerrarConversacionActive(notificarCliente: boolean = true): Promise<any> {
    const chat = this.selectedChatState();
    if (!chat) return Promise.reject('No active chat');
    const waId = this.getChatWaId(chat);
    if (!waId) return Promise.reject('No active chat sender');

    const conversationId = this.getSelectedConversationId();
    if (!conversationId) {
      return Promise.reject('No active conversation');
    }

    return new Promise((resolve, reject) => {
      this.apiService
        .cerrarChat(conversationId.toString(), waId, notificarCliente)
        .subscribe({
          next: (res) => {
            resolve(res);
          },
          error: (err) => reject(err),
        });
    });
  }

  enviarEncuestaActive(): Promise<any> {
    const chat = this.selectedChatState();
    if (!chat) return Promise.reject('No active chat');
    const waId = this.getChatWaId(chat);
    if (!waId) return Promise.reject('No active chat sender');

    const conversationId = this.getSelectedConversationId();
    if (!conversationId) {
      return Promise.reject('No active conversation');
    }

    return new Promise((resolve, reject) => {
      this.apiService
        .enviarEncuesta(conversationId.toString(), waId)
        .subscribe({
          next: (res) => {
            resolve(res);
          },
          error: (err) => reject(err),
        });
    });
  }

  derivarConversacionActive(
    areaId: number,
    usuarioDestino: string | null,
    observacion: string,
  ): Promise<DerivacionResponse> {
    const chat = this.selectedChatState();
    if (!chat) {
      return Promise.reject(
        new Error('No hay un chat seleccionado para derivar.'),
      );
    }

    const normalizedObservation = (observacion || '').trim();
    if (!normalizedObservation) {
      return Promise.reject(
        new Error('La observacion es obligatoria para derivar.'),
      );
    }

    if (chat.estadoConversacion !== 'ABIERTA') {
      return Promise.reject(
        new Error('Solo se puede derivar una conversacion abierta.'),
      );
    }

    const normalizedUser = (usuarioDestino || '').trim();
    const waId = this.getChatWaId(chat);
    if (!waId) {
      return Promise.reject(
        new Error('No se pudo identificar el contacto del chat.'),
      );
    }
    const conversationId = this.getSelectedConversationId();

    const requestBase: DerivacionRequest = {
      areaId,
      observacion: normalizedObservation,
      usuarioDestino: normalizedUser || undefined,
    };

    return new Promise((resolve, reject) => {
      const derivacion$ =
        conversationId > 0
          ? this.apiService.derivarConversacionById(conversationId, requestBase)
          : this.apiService.derivarConversacionByWaId({
              ...requestBase,
              waId,
            });

      derivacion$.subscribe({
        next: (response) => {
          this.applyConversationDerivacion(response, true);
          resolve(response);
        },
        error: (err) => reject(err),
      });
    });
  }

  registerSelfAssignmentPopupSuppression(
    conversacionId: number | null | undefined,
    waId: string | null | undefined,
    username: string,
  ): void {
    this.cleanupSelfAssignmentPopupSuppressions();
    const key = this.selfAssignmentPopupSuppressionKey(waId);
    const normalizedUsername = (username || '').trim().toLowerCase();
    if (!key || !normalizedUsername) return;

    const id = Number(conversacionId ?? 0);
    this.selfAssignmentPopupSuppressions.set(key, {
      username: normalizedUsername,
      conversacionId: id > 0 ? id : null,
      expiresAt: Date.now() + this.selfAssignmentPopupSuppressionDurationMs,
    });
  }

  consumeSelfAssignmentPopupSuppression(
    event: ConversationDerivacionPopupSseEvent,
  ): boolean {
    this.cleanupSelfAssignmentPopupSuppressions();
    const key = this.selfAssignmentPopupSuppressionKey(event.waId);
    if (!key) return false;

    const suppression = this.selfAssignmentPopupSuppressions.get(key);
    if (!suppression) return false;

    const eventConversationId = Number(event.conversacionId ?? 0);
    if (
      suppression.conversacionId !== null &&
      eventConversationId > 0 &&
      suppression.conversacionId !== eventConversationId
    ) {
      return false;
    }

    if ((event.usuarioAsignado || '').trim().toLowerCase() !== suppression.username) {
      return false;
    }

    this.selfAssignmentPopupSuppressions.delete(key);
    return true;
  }

  clearSelfAssignmentPopupSuppression(
    waId: string | null | undefined,
    username: string,
  ): void {
    const key = this.selfAssignmentPopupSuppressionKey(waId);
    const suppression = key ? this.selfAssignmentPopupSuppressions.get(key) : null;
    if (suppression?.username === (username || '').trim().toLowerCase()) {
      this.selfAssignmentPopupSuppressions.delete(key!);
    }
  }

  private selfAssignmentPopupSuppressionKey(
    waId: string | null | undefined,
  ): string | null {
    const normalizedWaId = this.normalizeWaId(waId);
    return normalizedWaId ? `wa:${normalizedWaId}` : null;
  }

  private cleanupSelfAssignmentPopupSuppressions(): void {
    const now = Date.now();
    for (const [key, suppression] of this.selfAssignmentPopupSuppressions.entries()) {
      if (suppression.expiresAt <= now) {
        this.selfAssignmentPopupSuppressions.delete(key);
      }
    }
  }

  private loadNumerosAndChats(): void {
    this.apiService.obtenerNumerosPropios().subscribe({
      next: (numeros: Numero[]) => {
        this.listaNumerosState.set(numeros);
        const activo = numeros.find((n) => n.activo) ?? null;
        this.numeroActualState.set(activo);
        this.refreshChats();
      },
      error: () => {
        this.refreshChats();
      },
    });
  }

  private applyIncomingMessage(message: Message): void {
    const chats = this.chatsState();
    const waIdContacto = this.getMessageContactWaId(message);
    const normalizedWaIdContacto = this.normalizeWaId(waIdContacto);

    let chatIndex = chats.findIndex(
      (c) => this.getChatWaId(c) === normalizedWaIdContacto,
    );

    if (chatIndex === -1 && message.conversacion?.id) {
      chatIndex = chats.findIndex(
        (c) => c.conversacion?.id === message.conversacion?.id,
      );
    }

    const esChatActual = this.isMessageForActiveChat(
      message,
      normalizedWaIdContacto,
    );

    const selectedChat = this.selectedChatState();
    const activeChatWaId = this.getChatWaId(selectedChat);
    const readWaId = normalizedWaIdContacto || activeChatWaId;
    const shouldMarkActiveChatAsRead =
      esChatActual &&
      message.autoria === 'CLIENTE' &&
      selectedChat?.accessMode === 'FULL' &&
      !!readWaId &&
      !this.manualUnreadPendingWaIds.has(readWaId);

    if (shouldMarkActiveChatAsRead) {
      this.markAsReadByWaId(readWaId);
    }

    if (
      !esChatActual &&
      this.isIncomingExternalMessage(message) &&
      this.shouldToastExternalMessage(message)
    ) {
      this.toastSseEvent('Nuevo mensaje', waIdContacto, message.sender?.name);
    }

    // Update messages if it's the active chat
    if (esChatActual) {
      if (message.conversacion && message.conversacion.id) {
        this.actualConversationNumberState.set(message.conversacion.id);
      }
      this.messagesState.update((msgs) => {
        const incomingKey = this.getMessageUniqueKey(message);
        const exists = msgs.some(
          (item, index) => this.getMessageUniqueKey(item, index) === incomingKey,
        );

        return exists ? msgs : [...msgs, message];
      });

      if (message.autoria === 'CLIENTE' && message.type === 'audio') {
        this.scheduleIncomingAudioHydration(message, normalizedWaIdContacto);
      }
    }

    if (chatIndex === -1) {
      this.refreshChats();
      return;
    }

    const updatedChats = [...chats];

    const previousChat = updatedChats[chatIndex];
    let newEstadoConversacion: 'ABIERTA' | 'ESPERANDO_ENCUESTA' | 'CERRADA' =
      previousChat.estadoConversacion ??
      this.getEstadoConversacion(previousChat.conversacion);
    let newConversacion = previousChat.conversacion;

    if (message.conversacion) {
      const incomingConversation =
        message.conversacion as Partial<Conversation>;
      newConversacion = this.mergeConversation(
        previousChat.conversacion,
        incomingConversation,
      );
      newEstadoConversacion = this.resolveEstadoConversacion(
        newConversacion,
        newEstadoConversacion,
        incomingConversation,
      );
    }

    updatedChats[chatIndex] = {
      ...updatedChats[chatIndex],
      lastMessage: message,
      conversacion: newConversacion,
      estadoConversacion: newEstadoConversacion,
      sender: {
        ...updatedChats[chatIndex].sender,
      },
    };

    // Si viene la conversacion entera actualizada en lastMessage lo pasamos (esto ya lo haria el backend)
    // Para mantener consistencia reactiva total del sidebar, repriorizamos los chats:
    const chatActualizado = updatedChats.splice(chatIndex, 1)[0];
    updatedChats.unshift(chatActualizado);
    this.chatsState.set(updatedChats);

    if (esChatActual) {
      // Actulizar puntero selectedChatState por si cambió estadoConversacion u otra metadata
      this.selectedChatState.set(chatActualizado);
    }

    this.updateTitle();
  }

  private scheduleIncomingAudioHydration(message: Message, waId: string): void {
    const messageKey = this.getMessageUniqueKey(message);
    const timerKey = `${waId}:${messageKey}`;
    const existingTimer = this.incomingAudioHydrationTimers.get(timerKey);
    if (existingTimer) {
      clearTimeout(existingTimer);
    }

    const timer = setTimeout(() => {
      this.incomingAudioHydrationTimers.delete(timerKey);
      if (this.activeChatWaIdState() !== waId) {
        return;
      }

      const selectedChat = this.selectedChatState();
      const conversationId = Number(selectedChat?.conversacion?.id ?? 0);
      const exactWaId = selectedChat?.conversacion?.waId || waId;
      const pageRequest = conversationId > 0
        ? this.apiService.obtenerMensajesMasAntiguos(exactWaId, conversationId, null, null, this.initialMessagesPageSize)
        : this.apiService.getMessages(waId, this.initialMessagesPageSize);

      pageRequest.subscribe({
        next: (page) => {
          const hydrated = (page.content || []).find(
            (candidate, index) => this.getMessageUniqueKey(candidate, index) === messageKey,
          );
          if (!hydrated?.media?.mediaUrl) {
            return;
          }
          this.messagesState.update((messages) =>
            messages.map((current, index) =>
              this.getMessageUniqueKey(current, index) === messageKey ? hydrated : current,
            ),
          );
        },
        error: (error) => console.warn('No se pudo hidratar el audio recibido en tiempo real:', error),
      });
    }, 400);

    this.incomingAudioHydrationTimers.set(timerKey, timer);
  }

  /**
   * Reproducir sonido de notificación
   * Método centralizado para evitar repetición de código
   * @param soundPath ruta del archivo de sonido
   * @param errorContext descripción del contexto para logging
   */
  private reproducirSonido(soundPath: string, errorContext: string = 'notificación'): void {
    if (this.userPresenceActivityService.isBusy()) {
      return;
    }

    this.notificationAudioService.play(soundPath, errorContext);
  }

  /**
   * Extraer ID del usuario actual desde el JWT
   */
  private loadCurrentUserFromToken(): void {
    try {
      const username = sessionStorage.getItem('username');
      if (username) {
        this.currentUsernameState.set(username);
      }
    } catch (error) {
      console.error('Error recuperando usuario en ChatRealtimeStoreService:', error);
    }
  }

  /**
   * Verificar si el mensaje interno es del usuario actual
   */
  private isOutgoingInternalMessage(message: InternalChatMessage): boolean {
    const currentUserId = this.internalChatStore.currentUserId();
    if (currentUserId !== null) {
      return message.senderUserId === currentUserId;
    }

    const currentUsername = this.currentUsernameState();
    return currentUsername && message.senderUsername === currentUsername ? true : false;
  }

  /**
   * Emitir sonido de notificación para mensajes de CLIENTE
   * NO reproduce sonido si el mensaje es de tipo 'menu' (flujo automático)
   */
  private emitirSonidoCliente(message: Message): void {
    // No hacer sonido si es type 'menu' (flujo automático rápido)
    if (message.type === 'menu') {
      return;
    }

    this.reproducirSonido('/assets/notification/notificacion_1.mp3', 'cliente');
    
    // Mostrar notificación del sistema en paralelo
    const senderName = message.sender?.name || 'Cliente';
    const messagePreview = this.getExternalMessageNotificationPreview(message);
    this.notificationSystemService.notifyExternalMessage(senderName, messagePreview);
  }

  /**
   * Emitir sonido de notificación para mensajes INTERNOS
   * SOLO reproducir sonido si el mensaje es de OTROS usuarios (no del actual)
   * @param message mensaje interno recibido
   */
  private procesarNotificacionesInternasPendientes(): void {
    if (this.internalChatStore.currentUserId() === null) return;

    const pendingMessages = this.pendingInternalNotifications;
    this.pendingInternalNotifications = [];
    pendingMessages.forEach((message) => this.procesarSonidoInterno(message));
  }

  private emitirSonidoInterno(message: InternalChatMessage): void {
    if (this.internalChatStore.currentUserId() === null) {
      if (this.pendingInternalNotifications.length >= 1000) {
        this.pendingInternalNotifications.shift();
      }
      this.pendingInternalNotifications.push(message);
      return;
    }

    this.procesarSonidoInterno(message);
  }

  private procesarSonidoInterno(message: InternalChatMessage): void {
    // No hacer sonido si es un mensaje saliente (del usuario actual)
    if (this.isOutgoingInternalMessage(message)) {
      return;
    }
    const messageId = message.messageId?.trim();
    if (messageId && this.notifiedInternalMessageIds.has(messageId)) return;
    if (messageId) {
      this.notifiedInternalMessageIds.add(messageId);
      if (this.notifiedInternalMessageIds.size > 1000) {
        const oldestId = this.notifiedInternalMessageIds.values().next().value;
        if (oldestId) this.notifiedInternalMessageIds.delete(oldestId);
      }
    }

    const mentioned = message.mentions?.some(mention => mention.userId === this.internalChatStore.currentUserId()) ?? false;
    this.reproducirSonido(
      mentioned
        ? '/assets/notification/mencion.ogg'
        : '/assets/notification/notificacion_interna.mp3',
      mentioned ? 'mención interna' : 'interna',
    );

    // Mostrar notificación del sistema en paralelo
    const senderName = message.senderNombreCompleto || message.senderUsername || 'Usuario interno';
    const messagePreview = this.getInternalMessageNotificationPreview(message);
    if (mentioned) {
      this.notificationSystemService.notifyInternalMention(senderName, messagePreview);
    } else {
      this.notificationSystemService.notifyInternalMessage(senderName, messagePreview);
    }
  }

  private getExternalMessageNotificationPreview(message: Message): string {
    if (message.type === 'image' || message.type === 'video') {
      const caption = message.media?.caption?.trim();
      if (caption) {
        return caption;
      }

      return message.type === 'image' ? 'Foto' : 'Video';
    }

    return message.body?.trim() || '[Archivo adjunto]';
  }

  private getInternalMessageNotificationPreview(
    message: InternalChatMessage,
  ): string {
    const body = message.body?.trim();
    if (body) {
      return body;
    }

    if (message.messageType === 'IMAGE') {
      return 'Foto';
    }

    if (message.messageType === 'VIDEO') {
      return 'Video';
    }

    return '[Mensaje vacío]';
  }

  /**
   * Método público para reproducir sonido genérico (usado externamente si es necesario)
   */
  emitirSonido(): void {
    this.reproducirSonido('/assets/notification/notificacion_1.mp3', 'generica');
  }

  private getEstadoConversacion(
    conversation: Conversation | null | undefined,
  ): 'ABIERTA' | 'ESPERANDO_ENCUESTA' | 'CERRADA' {
    if (!conversation?.isClosed) {
      return 'ABIERTA';
    }

    return conversation.isPendienteEnvioEncuesta
      ? 'ESPERANDO_ENCUESTA'
      : 'CERRADA';
  }

  private normalizeEstadoConversacion(
    value?: string | null,
  ): 'ABIERTA' | 'ESPERANDO_ENCUESTA' | 'CERRADA' | null {
    const normalized = String(value || '')
      .trim()
      .toUpperCase();
    if (
      normalized === 'ABIERTA' ||
      normalized === 'ESPERANDO_ENCUESTA' ||
      normalized === 'CERRADA'
    ) {
      return normalized;
    }

    return null;
  }

  private getEstadoConversacionFromFlags(
    closed: boolean,
    pendienteEnvioEncuesta: boolean,
  ): 'ABIERTA' | 'ESPERANDO_ENCUESTA' | 'CERRADA' {
    if (!closed) {
      return 'ABIERTA';
    }

    return pendienteEnvioEncuesta ? 'ESPERANDO_ENCUESTA' : 'CERRADA';
  }

  private resolveEstadoConversacionFromSse(
    event: ConversationStateSseEvent,
    fallbackConversation: Conversation,
    fallbackEstado: 'ABIERTA' | 'ESPERANDO_ENCUESTA' | 'CERRADA',
  ): 'ABIERTA' | 'ESPERANDO_ENCUESTA' | 'CERRADA' {
    const estadoFromEvent = this.normalizeEstadoConversacion(
      event.estadoConversacion,
    );
    if (estadoFromEvent) {
      return estadoFromEvent;
    }

    if (typeof event.closed === 'boolean') {
      return this.getEstadoConversacionFromFlags(
        event.closed,
        !!event.pendienteEnvioEncuesta,
      );
    }

    return fallbackEstado || this.getEstadoConversacion(fallbackConversation);
  }

  private ownershipFromStateEvent(event: ConversationStateSseEvent, conversation: Conversation): Partial<Conversation> {
    if (event.closed || ('usuarioAsignado' in event && !event.usuarioAsignado)) {
      return { usuarioAsignado: null, nombreCompletoAsignado: null, rolAsignado: null, fechaDerivacion: null };
    }
    const patch: Partial<Conversation> = {};
    if ('usuarioAsignado' in event) {
      patch.usuarioAsignado = event.usuarioAsignado;
      patch.nombreCompletoAsignado = event.usuarioAsignado === conversation?.usuarioAsignado
        ? conversation?.nombreCompletoAsignado : null;
    }
    if ('rolAsignado' in event) patch.rolAsignado = event.rolAsignado;
    if ('fechaDerivacion' in event) patch.fechaDerivacion = event.fechaDerivacion;
    return patch;
  }

  private applyConversationStateChanged(
    event: ConversationStateSseEvent,
  ): void {
    const normalizedEventWaId = this.normalizeWaId(event.waId);
    const eventConversationId = Number(event.conversationId ?? 0);

    this.chatsState.update((currentChats) =>
      currentChats.map((chat) => {
        const normalizedChatWaId = this.getChatWaId(chat);
        const sameWaId =
          !!normalizedEventWaId && normalizedChatWaId === normalizedEventWaId;
        const sameConversation =
          eventConversationId > 0 &&
          chat.conversacion?.id === eventConversationId;

        if ((!sameWaId && !sameConversation) ||
            (eventConversationId > 0 && Number(chat.conversacion?.id) > eventConversationId)) {
          return chat;
        }

        const updatedConversation: Conversation = {
          ...chat.conversacion,
          ...this.ownershipFromStateEvent(event, chat.conversacion),
          id:
            eventConversationId > 0
              ? eventConversationId
              : chat.conversacion?.id ?? 0,
          waId: event.waId || chat.conversacion?.waId || normalizedChatWaId,
          isClosed: event.closed,
          isPendienteEnvioEncuesta: event.pendienteEnvioEncuesta,
        };

        const currentEstado =
          chat.estadoConversacion ??
          this.getEstadoConversacion(chat.conversacion);

        return {
          ...chat,
          conversacion: updatedConversation,
          estadoConversacion: this.resolveEstadoConversacionFromSse(
            event,
            updatedConversation,
            currentEstado,
          ),
        };
      }),
    );

    const selectedChat = this.selectedChatState();
    if (!selectedChat) {
      return;
    }

    const selectedWaId = this.getChatWaId(selectedChat);
    const sameSelectedWaId =
      !!normalizedEventWaId && selectedWaId === normalizedEventWaId;
    const sameSelectedConversation =
      eventConversationId > 0 &&
      selectedChat.conversacion?.id === eventConversationId;

    if ((!sameSelectedWaId && !sameSelectedConversation) ||
        (eventConversationId > 0 && Number(selectedChat.conversacion?.id) > eventConversationId)) {
      return;
    }

    const updatedConversation: Conversation = {
      ...selectedChat.conversacion,
      ...this.ownershipFromStateEvent(event, selectedChat.conversacion),
      id:
        eventConversationId > 0
          ? eventConversationId
          : selectedChat.conversacion?.id ?? 0,
      waId: event.waId || selectedChat.conversacion?.waId || selectedWaId,
      isClosed: event.closed,
      isPendienteEnvioEncuesta: event.pendienteEnvioEncuesta,
    };

    const selectedEstado =
      selectedChat.estadoConversacion ??
      this.getEstadoConversacion(selectedChat.conversacion);

    this.selectedChatState.set({
      ...selectedChat,
      conversacion: updatedConversation,
      estadoConversacion: this.resolveEstadoConversacionFromSse(
        event,
        updatedConversation,
        selectedEstado,
      ),
    });

    if (eventConversationId > 0) {
      this.actualConversationNumberState.set(eventConversationId);
    }
  }

  private applySenderUnreadChanged(event: SenderUnreadSseEvent): void {
    const normalizedEventWaId = this.normalizeWaId(event.waId);
    if (!normalizedEventWaId) {
      return;
    }

    this.clearUnreadReconcileTimer(normalizedEventWaId);

    if (
      this.manualUnreadPendingWaIds.has(normalizedEventWaId) &&
      !event.mensajesNuevos
    ) {
      this.updateTitle();
      return;
    }

    this.setUnreadStateByWaId(normalizedEventWaId, event.mensajesNuevos);

    this.updateTitle();
  }

  private applyForceExitMenuResponse(response: {
    conversationId: number;
    waId: string;
    menuActivo: boolean;
    pendienteEnvioMensaje: boolean;
    closed: boolean;
  }): void {
    const normalizedResponseWaId = this.normalizeWaId(response.waId);
    const responseConversationId = Number(response.conversationId ?? 0);

    this.chatsState.update((currentChats) =>
      currentChats.map((chat) => {
        const sameWaId =
          !!normalizedResponseWaId &&
          this.getChatWaId(chat) === normalizedResponseWaId;
        const sameConversation =
          responseConversationId > 0 &&
          chat.conversacion?.id === responseConversationId;

        if (!sameWaId && !sameConversation) {
          return chat;
        }

        const updatedConversation: Conversation = {
          ...chat.conversacion,
          id:
            responseConversationId > 0
              ? responseConversationId
              : chat.conversacion?.id ?? 0,
          waId: response.waId || chat.conversacion?.waId,
          menuActivo: response.menuActivo,
          isPendienteEnvioMensaje: response.pendienteEnvioMensaje,
          isClosed: response.closed,
        };

        return {
          ...chat,
          conversacion: updatedConversation,
          estadoConversacion: this.getEstadoConversacion(updatedConversation),
        };
      }),
    );

    const selectedChat = this.selectedChatState();
    if (!selectedChat) {
      return;
    }

    const sameSelectedWaId =
      !!normalizedResponseWaId &&
      this.getChatWaId(selectedChat) === normalizedResponseWaId;
    const sameSelectedConversation =
      responseConversationId > 0 &&
      selectedChat.conversacion?.id === responseConversationId;

    if (!sameSelectedWaId && !sameSelectedConversation) {
      return;
    }

    const updatedConversation: Conversation = {
      ...selectedChat.conversacion,
      id:
        responseConversationId > 0
          ? responseConversationId
          : selectedChat.conversacion?.id ?? 0,
      waId: response.waId || selectedChat.conversacion?.waId,
      menuActivo: response.menuActivo,
      isPendienteEnvioMensaje: response.pendienteEnvioMensaje,
      isClosed: response.closed,
    };

    this.selectedChatState.set({
      ...selectedChat,
      conversacion: updatedConversation,
      estadoConversacion: this.getEstadoConversacion(updatedConversation),
    });

    if (responseConversationId > 0) {
      this.actualConversationNumberState.set(responseConversationId);
    }
  }

  private applyConversationDerivacion(
    event: ConversationDerivacionSseEvent,
    fromLocalAction: boolean = false,
  ): void {
    const conversationId = Number(event.conversacionId ?? 0);
    const isActiveContext = this.isActiveChatContext(
      event.waId,
      conversationId,
    );
    const derivacionPatch = this.applyDerivacionSidebarPreview(
      event,
      isActiveContext,
    );

    if (
      derivacionPatch.systemMessage &&
      derivacionPatch.appliedToSelectedChat
    ) {
      this.upsertDerivacionMessageInActiveThread(derivacionPatch.systemMessage);
    }

    if (
      conversationId > 0 &&
      (isActiveContext || derivacionPatch.appliedToSelectedChat)
    ) {
      this.actualConversationNumberState.set(conversationId);
    }

    this.refreshChats();
  }

  private applySidebarChanged(_event: SidebarSseEvent): void {
    this.refreshChats();
  }

  private mergeConversation(
    base: Conversation,
    incoming: Partial<Conversation>,
  ): Conversation {
    const rawIncoming = incoming as Partial<Conversation> & { closed?: boolean };
    const normalizedIncoming: Partial<Conversation> = {
      ...incoming,
      ...(typeof rawIncoming.closed === 'boolean'
        ? { isClosed: rawIncoming.closed }
        : {}),
    };
    return {
      ...base,
      ...normalizedIncoming,
      area: {
        ...base.area,
        ...(normalizedIncoming.area ?? {}),
      },
    };
  }

  private resolveEstadoConversacion(
    conversation: Conversation,
    previousEstado: 'ABIERTA' | 'ESPERANDO_ENCUESTA' | 'CERRADA',
    incoming?: Partial<Conversation>,
  ): 'ABIERTA' | 'ESPERANDO_ENCUESTA' | 'CERRADA' {
    // Si el evento no trae isClosed explícito, no debemos sobreescribir el estado actual.
    const hasClosedFlag = incoming && (
      typeof incoming.isClosed === 'boolean' ||
      typeof (incoming as Partial<Conversation> & { closed?: boolean }).closed === 'boolean'
    );
    if (incoming && !hasClosedFlag) {
      return previousEstado;
    }

    if (!conversation.isClosed) {
      return 'ABIERTA';
    }

    if (incoming && typeof incoming.isPendienteEnvioEncuesta !== 'boolean') {
      if (
        previousEstado === 'CERRADA' ||
        previousEstado === 'ESPERANDO_ENCUESTA'
      ) {
        return previousEstado;
      }
    }

    return conversation.isPendienteEnvioEncuesta
      ? 'ESPERANDO_ENCUESTA'
      : 'CERRADA';
  }

  private applyStatusUpdate(status: Status): void {
    if (status.wasApplied !== true) {
      console.warn('[SSE] MESSAGE_STATUS ignorado porque wasApplied != true:', {
        messageId: status.messageId,
        wasApplied: status.wasApplied,
        rejectionReason: status.rejectionReason,
      });
      return;
    }

    const statusWaId = this.normalizeWaId(
      status.conversacion?.waId || status.waId || '',
    );
    const activeWaId = this.normalizeWaId(this.activeChatWaIdState() || '');
    const statusConversationId = status.conversacion?.id ?? 0;
    const activeConversationId = this.actualConversationNumberState();

    const sameWaId = !!statusWaId && !!activeWaId && statusWaId === activeWaId;
    const sameConversation =
      statusConversationId > 0 &&
      activeConversationId > 0 &&
      statusConversationId === activeConversationId;

    if (!sameWaId && !sameConversation) {
      return;
    }

    if (status.conversacion && status.conversacion.id) {
      this.actualConversationNumberState.set(status.conversacion.id);
    }

    this.messagesState.update((msgs) => {
      const idx = msgs.findIndex(
        (m) => String(m.messageId) === String(status.messageId),
      );
      if (idx !== -1) {
        const updated = [...msgs];
        updated[idx] = {
          ...updated[idx],
          status: status.status,
          failureCategory: status.failureCategory ?? updated[idx].failureCategory,
          failureCode: status.failureCode ?? updated[idx].failureCode,
          failureTitle: status.failureTitle ?? updated[idx].failureTitle,
          failureTraceId: status.failureTraceId ?? updated[idx].failureTraceId,
          failureMessage: status.failureMessage ?? updated[idx].failureMessage,
        };
        return updated;
      }
      return msgs;
    });
  }

  private scheduleUnreadReconcile(normalizedWaId: string): void {
    const timeoutId = setTimeout(() => {
      this.unreadReconcileTimers.delete(normalizedWaId);

      const stillUnread = this.chatsState().some(
        (chat) =>
          this.getChatWaId(chat) === normalizedWaId &&
          chat?.sender?.mensajesNuevos,
      );

      if (stillUnread) {
        this.refreshChats();
      }
    }, this.unreadReconcileDelayMs);

    this.unreadReconcileTimers.set(normalizedWaId, timeoutId);
  }

  private clearUnreadReconcileTimer(normalizedWaId: string): void {
    const timeoutId = this.unreadReconcileTimers.get(normalizedWaId);
    if (!timeoutId) {
      return;
    }

    clearTimeout(timeoutId);
    this.unreadReconcileTimers.delete(normalizedWaId);
  }

  private getMessageContactWaId(message: Message): string {
    if (message.autoria === 'CLIENTE') {
      return (
        message.sender?.waId ||
        message.conversacion?.waId ||
        message.receiver ||
        ''
      );
    }

    return (
      message.conversacion?.waId ||
      message.receiver ||
      message.sender?.waId ||
      ''
    );
  }

  private isIncomingExternalMessage(message: Message): boolean {
    return message.autoria === 'CLIENTE';
  }

  private shouldToastExternalMessage(message: Message): boolean {
    const messageKey =
      String(message.messageId || '').trim() ||
      [
        this.getMessageContactWaId(message),
        message.timestamp || '',
        message.body || '',
      ].join('|');

    if (!messageKey) {
      return true;
    }

    if (this.notifiedExternalMessageIds.has(messageKey)) {
      return false;
    }

    this.notifiedExternalMessageIds.add(messageKey);
    return true;
  }

  private toastSseEvent(
    eventLabel: string,
    waId?: string,
    fallbackName?: string,
  ): void {
    const senderName = this.resolveSenderDisplayName(waId, fallbackName);
    this.toastService.notify(`${eventLabel} de ${senderName}`, 'info', 3500);
  }

  private resolveSenderDisplayName(
    waId?: string,
    fallbackName?: string,
  ): string {
    const normalizedWaId = this.normalizeWaId(waId || '');
    if (normalizedWaId) {
      const chat = this.chatsState().find(
        (item) => this.getChatWaId(item) === normalizedWaId,
      );

      if (chat?.sender?.name) {
        return chat.sender.name;
      }
    }

    return fallbackName || 'Contacto';
  }

  private isMessageForActiveChat(
    message: Message,
    normalizedMessageWaId: string,
  ): boolean {
    const activeWaId = this.normalizeWaId(this.activeChatWaIdState() || '');
    const selectedConversationId =
      this.selectedChatState()?.conversacion?.id ?? 0;
    const activeConversationId = this.actualConversationNumberState();
    const messageConversationId = message.conversacion?.id ?? 0;
    const messageConversationWaId = this.normalizeWaId(
      message.conversacion?.waId || '',
    );

    const sameWaId =
      !!activeWaId &&
      !!normalizedMessageWaId &&
      activeWaId === normalizedMessageWaId;
    const sameConversationWaId =
      !!activeWaId &&
      !!messageConversationWaId &&
      activeWaId === messageConversationWaId;
    const targetConversationId =
      activeConversationId > 0 ? activeConversationId : selectedConversationId;
    const sameConversation =
      targetConversationId > 0 &&
      messageConversationId > 0 &&
      targetConversationId === messageConversationId;

    return sameWaId || sameConversationWaId || sameConversation;
  }

  private isActiveChatContext(waId?: string, conversationId?: number): boolean {
    const activeWaId = this.normalizeWaId(this.activeChatWaIdState() || '');
    const normalizedTargetWaId = this.normalizeWaId(waId || '');

    const selectedConversationId =
      this.selectedChatState()?.conversacion?.id ?? 0;
    const activeConversationId = this.actualConversationNumberState();
    const targetConversationId = Number(conversationId ?? 0);
    const currentConversationId =
      activeConversationId > 0 ? activeConversationId : selectedConversationId;

    const sameWaId =
      !!activeWaId &&
      !!normalizedTargetWaId &&
      activeWaId === normalizedTargetWaId;
    const sameConversation =
      currentConversationId > 0 &&
      targetConversationId > 0 &&
      currentConversationId === targetConversationId;

    return sameWaId || sameConversation;
  }

  private applyDerivacionSidebarPreview(
    event: ConversationDerivacionSseEvent,
    isActiveContext: boolean,
  ): { systemMessage: Message | null; appliedToSelectedChat: boolean } {
    const chats = this.chatsState();
    const normalizedEventWaId = this.normalizeWaId(event.waId);
    const eventConversationId = Number(event.conversacionId ?? 0);
    const selectedChat = this.selectedChatState();

    const selectedMatchesEvent =
      !!selectedChat &&
      this.isChatMatchingDerivacionEvent(
        this.getChatWaId(selectedChat),
        Number(selectedChat.conversacion?.id ?? 0),
        normalizedEventWaId,
        eventConversationId,
      );

    let chatIndex = chats.findIndex(
      (chat) => this.getChatWaId(chat) === normalizedEventWaId,
    );

    if (chatIndex === -1 && eventConversationId > 0) {
      chatIndex = chats.findIndex(
        (chat) => chat.conversacion?.id === eventConversationId,
      );
    }

    const baseChat =
      chatIndex !== -1
        ? chats[chatIndex]
        : selectedMatchesEvent
          ? selectedChat
          : null;

    if (!baseChat) {
      return {
        systemMessage: null,
        appliedToSelectedChat: false,
      };
    }

    const updatedConversation: Conversation = {
      ...baseChat.conversacion,
      id:
        eventConversationId > 0
          ? eventConversationId
          : baseChat.conversacion.id,
      waId: event.waId || baseChat.conversacion.waId,
      menuActivo: false,
      area: {
        ...baseChat.conversacion.area,
        ...(typeof event.areaId === 'number' ? { id: event.areaId } : {}),
        ...(event.areaDescripcion
          ? { descripcion: event.areaDescripcion }
          : {}),
      },
      usuarioAsignado: event.asignada ? event.usuarioAsignado : null,
      nombreCompletoAsignado: event.asignada ? event.nombreCompletoAsignado : null,
      rolAsignado: event.asignada ? event.rolAsignado : null,
      fechaDerivacion:
        event.asignada ? event.fechaDerivacion ?? baseChat.conversacion.fechaDerivacion : null,
    };

    const previewText = this.getDerivacionPreviewText(event);
    const systemMessage = this.buildSystemDerivacionMessage(
      baseChat,
      updatedConversation,
      previewText,
      event,
    );

    const updatedChat: Chat = {
      ...baseChat,
      conversacion: updatedConversation,
      lastMessage: systemMessage,
      estadoConversacion:
        baseChat.estadoConversacion ??
        this.getEstadoConversacion(updatedConversation),
    };

    if (chatIndex !== -1) {
      const updatedChats = [...chats];
      updatedChats.splice(chatIndex, 1);
      updatedChats.unshift(updatedChat);
      this.chatsState.set(updatedChats);
    }

    let appliedToSelectedChat = false;
    if ((isActiveContext || selectedMatchesEvent) && selectedChat) {
      this.selectedChatState.set({
        ...selectedChat,
        conversacion: updatedConversation,
        lastMessage: systemMessage,
        estadoConversacion:
          selectedChat.estadoConversacion ??
          this.getEstadoConversacion(updatedConversation),
      });
      appliedToSelectedChat = true;
    }

    return {
      systemMessage,
      appliedToSelectedChat,
    };
  }

  private isChatMatchingDerivacionEvent(
    chatWaId: string,
    chatConversationId: number,
    normalizedEventWaId: string,
    eventConversationId: number,
  ): boolean {
    const normalizedChatWaId = this.normalizeWaId(chatWaId);
    const sameWaId =
      !!normalizedEventWaId && normalizedChatWaId === normalizedEventWaId;
    const sameConversation =
      chatConversationId > 0 &&
      eventConversationId > 0 &&
      chatConversationId === eventConversationId;

    return sameWaId || sameConversation;
  }

  private upsertDerivacionMessageInActiveThread(systemMessage: Message): void {
    this.messagesState.update((currentMessages) => {
      const exists = currentMessages.some((message) =>
        this.isSameDerivacionSystemMessage(message, systemMessage),
      );

      if (exists) {
        return currentMessages;
      }

      return [...currentMessages, systemMessage];
    });
  }

  private isSameDerivacionSystemMessage(
    current: Message,
    incoming: Message,
  ): boolean {
    if (String(current.messageId) === String(incoming.messageId)) {
      return true;
    }

    return (
      current.autoria === 'SISTEMA' &&
      incoming.autoria === 'SISTEMA' &&
      String(current.conversacion?.id || 0) ===
        String(incoming.conversacion?.id || 0) &&
      String(current.timestamp || '') === String(incoming.timestamp || '') &&
      String(current.body || '') === String(incoming.body || '')
    );
  }

  private getDerivacionPreviewText(
    event: ConversationDerivacionSseEvent,
  ): string {
    const observation = (event.observacion || '').trim();
    if (observation) {
      return observation;
    }

    const message = (event.mensaje || '').trim();
    if (message) {
      return message;
    }

    return event.areaDescripcion
      ? `Conversacion derivada a ${event.areaDescripcion}`
      : 'Conversacion derivada';
  }

  private buildSystemDerivacionMessage(
    baseChat: Chat,
    conversation: Conversation,
    body: string,
    event: ConversationDerivacionSseEvent,
  ): Message {
    const timestamp = event.fechaDerivacion || new Date().toISOString();
    const messageId = [
      'derivacion',
      String(event.conversacionId || conversation.id || 0),
      String(event.areaId || conversation.area?.id || 0),
      timestamp,
    ].join('-');

    return {
      messageId,
      body,
      sender: baseChat.sender,
      timestamp,
      receiver: event.waId || this.getChatWaId(baseChat),
      type: 'text',
      media: {
        id: 0,
        mimeType: '',
        mediaUrl: '',
        thumbnailUrl: '',
      },
      status: 'read',
      autoria: 'SISTEMA',
      usuario: event.usuarioAsignado || 'Sistema',
      conversacion: conversation,
    };
  }

  private updateTitle(): void {
    const unOpen = this.unreadCount();
    const title = unOpen > 0 ? '(' + unOpen + ') SGW' : 'SGW';

    this.titleService.setTitle(title);
  }
}
